import React, { useState, useEffect } from "react"
import { useMutation } from "urql"

import { FilePicker } from "../../../../components/shared/FileStackWidgets"
import { useToast } from "../../../../contexts/ToastContext"
import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"
import ConfirmSaveModal from "../shared/ConfirmSaveModal"
import ImagePickerV2 from "../shared/ImagePickerV2"

import EditorFooter from "./shared/EditorFooter"

const MAX_GALLERY_IMAGES = 8

const GalleryEditor = ({ section, practice, onSave, onCancel, onDelete, isEditing = false }) => {
  const { handleUpdateSectionPreview, clearPreview } = useWebsiteBuilderV2()
  const [formData, setFormData] = useState({
    headline: section?.headline || "Gallery",
    subheadline: section?.subheadline || "Take a look at our work and environment",
    body: section?.body || "",
    settings: section?.settings || {
      layout: "carousel", // carousel, grid, bento
      showBackgroundColor: false,
      backgroundColor: "#ffffff",
      showCaptions: true,
      enableLightbox: true,
      lazyLoad: true,
      images: section?.imageUrls
        ? section.imageUrls.map((url) => ({ url, altText: "Gallery image", type: "image" }))
        : []
    }
  })
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  // GraphQL mutation for uploading images
  const UPLOAD_IMAGE_MUTATION = `
    mutation UploadImage($handle: String!, $description: String!, $provider: String) {
      uploadImage(handle: $handle, description: $description, provider: $provider) {
        result
        errors
        photoUrl
        altText
      }
    }
  `
  const [{ fetching: uploadFetching }, uploadImage] = useMutation(UPLOAD_IMAGE_MUTATION)
  const { showToast } = useToast()

  useEffect(() => {
    if (section) {
      // Handle migration from old imageUrls format to new settings.images format
      let images = []
      if (section.settings?.images) {
        images = section.settings.images
      } else if (section.imageUrls) {
        images = section.imageUrls.map((url) => ({ url, altText: "Gallery image", type: "image" }))
      }

      setFormData({
        headline: section.headline || "Gallery",
        subheadline: section.subheadline || "Take a look at our work and environment",
        body: section.body || "",
        settings: {
          layout: "carousel",
          showBackgroundColor: false,
          backgroundColor: "#ffffff",
          showCaptions: true,
          enableLightbox: true,
          lazyLoad: true,
          images,
          ...section.settings
        }
      })
    }
  }, [section, practice])

  const handleSettingsChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      settings: { ...prev.settings, [key]: value }
    }))

    // Update preview immediately for layout changes and other important settings
    if (
      section?.id &&
      ["layout", "columns", "showCaptions", "showBackgroundColor", "backgroundColor", "images"].includes(key)
    ) {
      const updatedSettings = { ...formData.settings, [key]: value }
      handleUpdateSectionPreview(section.id, {
        ...section,
        ...formData,
        settings: updatedSettings
      })
    }
  }

  // Handle image selection from ImagePickerV2
  const handleImageSelect = ({ handle, provider }) => {
    const already = (formData.settings.images || []).length
    if (already >= MAX_GALLERY_IMAGES) {
      showToast({ type: "error", content: `Maximum of ${MAX_GALLERY_IMAGES} images reached.` })
      return
    }

    uploadImage({
      handle: handle,
      description: "gallery_section_image",
      provider: provider
    })
      .then((result) => {
        if (result?.data?.uploadImage?.result === "success") {
          const newImage = {
            url: result.data.uploadImage.photoUrl,
            altText: result.data.uploadImage.altText || "Gallery image",
            type: "image"
          }
          const updatedImages = [...(formData.settings.images || []), newImage].slice(0, MAX_GALLERY_IMAGES)
          handleSettingsChange("images", updatedImages)
          showToast("Image added successfully!")
        } else {
          // eslint-disable-next-line no-console
          console.error(result)
          let errorMessage = "There was an error uploading your image. Please try again later or contact support."
          if (result.data?.uploadImage?.errors) errorMessage += ` ${result.data.uploadImage.errors}`
          showToast({ type: "error", content: errorMessage })
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error("Upload error:", error)
        showToast({ type: "error", content: "There was an error uploading your image. Please try again." })
      })
  }

  // Upload video (filestack returns url; store as type video)
  const handleVideoUpload = async (res) => {
    const already = (formData.settings.images || []).length
    const remaining = Math.max(0, MAX_GALLERY_IMAGES - already)
    const files = (res?.filesUploaded || []).slice(0, remaining)
    if (files.length === 0) {
      showToast({ type: "error", content: `Maximum of ${MAX_GALLERY_IMAGES} items reached.` })
      return
    }

    const newVideos = files.map((f) => ({ url: f.url, altText: "Gallery video", type: "video" }))
    const updated = [...(formData.settings.images || []), ...newVideos].slice(0, MAX_GALLERY_IMAGES)
    handleSettingsChange("images", updated)
    showToast(`${newVideos.length} video${newVideos.length > 1 ? "s" : ""} added successfully!`)
  }

  // Handle image removal
  const handleImageRemove = (indexToRemove) => {
    const updatedImages = (formData.settings.images || []).filter((_, index) => index !== indexToRemove)
    handleSettingsChange("images", updatedImages)
    showToast("Image removed.")
  }

  // Reorder helpers
  const moveImage = (index, delta) => {
    const items = [...(formData.settings.images || [])]
    const newIndex = index + delta
    if (newIndex < 0 || newIndex >= items.length) return
    const [moved] = items.splice(index, 1)
    items.splice(newIndex, 0, moved)
    handleSettingsChange("images", items)
  }

  // Layout options with preview images
  const layoutOptions = [
    {
      id: "carousel",
      name: "Carousel",
      description: "Single image with navigation arrows",
      image: require("../../../../../assets/images/builder/v2/layout-images/carousel.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/carousel-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/carousel-selected.png")
    },
    {
      id: "grid",
      name: "Grid",
      description: "Uniform grid layout",
      image: require("../../../../../assets/images/builder/v2/layout-images/grid.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/grid-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/grid-selected.png")
    },
    {
      id: "bento",
      name: "Bento",
      description: "Mixed size grid layout",
      image: require("../../../../../assets/images/builder/v2/layout-images/bento.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/bento-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/bento-selected.png")
    }
  ]

  const LayoutOption = ({ option, isSelected, onSelect }) => {
    const [isHovered, setIsHovered] = useState(false)

    const getImageSource = () => {
      if (isSelected) {
        return option.imageSelected
      } else if (isHovered) {
        return option.imageHover
      } else {
        return option.image
      }
    }

    return (
      <div
        className="cursor-pointer transition-all"
        onClick={() => onSelect(option.id)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title={option.name}>
        <img
          src={getImageSource()}
          alt={`${option.name} layout`}
          className="h-16 w-24 rounded-lg object-cover transition-all duration-200"
          onError={(e) => {
            console.warn(`Failed to load image: ${e.target.src}`) // eslint-disable-line no-console
            e.target.style.display = "none"
            e.target.nextSibling.style.display = "block"
          }}
        />
        {/* Fallback placeholder */}
        <div className="hidden h-16 w-24 items-center justify-center rounded-lg bg-gray-200">
          <div className="text-xs text-gray-500">{option.name}</div>
        </div>
      </div>
    )
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.headline.trim()) {
      newErrors.headline = "Section headline is required"
    }

    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e?.preventDefault?.()
    if (validateForm()) {
      setShowConfirmModal(true)
    }
  }

  const handleConfirmSave = () => {
    setShowConfirmModal(false)
    onSave({
      layout: "gallery",
      ...formData
    })
  }

  const handleCancelClick = () => {
    clearPreview()
    onCancel()
  }

  return (
    <>
      {/* Form Content */}
      <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-8 pb-2 pt-6">
        {/* Layout Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Layout</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Select a layout design for this section:</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 content-start items-start justify-start gap-3 text-clip p-0 [flex-flow:wrap]">
            {layoutOptions.map((option) => (
              <LayoutOption
                key={option.id}
                option={option}
                isSelected={formData.settings.layout === option.id}
                onSelect={(layoutId) => handleSettingsChange("layout", layoutId)}
              />
            ))}
          </div>
        </div>

        {/* Images Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Images</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Select images to feature in this section.</p>
            </div>
          </div>

          {/* Image Upload Area */}
          {formData.settings.images && formData.settings.images.length > 0 ? (
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
              <div className="relative box-border flex w-full shrink-0 content-start items-start justify-start gap-4 text-clip p-0 [flex-flow:wrap]">
                {formData.settings.images.map((image, index) => (
                  <div key={index} className="group relative">
                    <div className="overflow-hidden rounded-lg border-2 border-[#c8d6df]">
                      {image.type === "video" ? (
                        <video src={image.url} className="h-24 w-24 object-cover" />
                      ) : (
                        <img
                          src={image.url}
                          alt={image.altText || `Gallery image ${index + 1}`}
                          className="h-24 w-24 object-cover"
                        />
                      )}
                      {/* Position badge */}
                      <div className="absolute left-2 top-2 rounded bg-black/60 px-1.5 py-0.5 text-xs font-semibold text-white">
                        {index + 1}
                      </div>
                      {/* Remove button */}
                      <div className="absolute right-2 top-2 flex gap-1">
                        <button
                          onClick={() => handleImageRemove(index)}
                          className="rounded-full bg-red-500 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100">
                          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                      {/* Reorder controls */}
                      <div className="absolute inset-x-0 bottom-1 flex items-center justify-between px-1 opacity-0 transition-opacity group-hover:opacity-100">
                        <button
                          className="rounded bg-white/80 px-1 text-xs text-[#0b3954] shadow"
                          onClick={() => moveImage(index, -1)}
                          disabled={index === 0}>
                          ←
                        </button>
                        <button
                          className="rounded bg-white/80 px-1 text-xs text-[#0b3954] shadow"
                          onClick={() => moveImage(index, 1)}
                          disabled={index === (formData.settings.images?.length || 1) - 1}>
                          →
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-center gap-2 p-0">
                <ImagePickerV2
                  practice={practice}
                  onImageSelect={handleImageSelect}
                  fetching={uploadFetching}
                  showCurateTab={true}
                  trigger={
                    <button
                      className="font-bold text-[#0d9da4] hover:underline"
                      disabled={uploadFetching || (formData.settings.images?.length || 0) >= MAX_GALLERY_IMAGES}>
                      Add more images
                    </button>
                  }
                />
                <span className="text-[#55778a]">or</span>
                <FilePicker
                  as="button"
                  className="font-bold text-[#0d9da4] hover:underline"
                  onUploadDone={handleVideoUpload}
                  disabled={uploadFetching}
                  maxFiles={Math.max(1, MAX_GALLERY_IMAGES - (formData.settings.images?.length || 0))}
                  accept={["video/*"]}
                  fromSources={["local_file_system", "url", "googledrive", "dropbox"]}>
                  Add video
                </FilePicker>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <ImagePickerV2
                practice={practice}
                onImageSelect={handleImageSelect}
                fetching={uploadFetching}
                showCurateTab={true}
                trigger={
                  <div className="relative box-border flex w-full shrink-0 cursor-pointer flex-col content-stretch items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-[#c8d6df] bg-white p-6 transition-colors hover:border-[#0d9da4]">
                    <div className="relative flex size-10 shrink-0 items-center justify-center text-clip rounded-[100px] bg-[#e2f3f4]">
                      <svg className="h-5 w-5 text-[#0d9da4]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                    </div>
                    <div className="relative shrink-0 text-nowrap text-center font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0d9da4]">
                      <p className="whitespace-pre leading-[24px]">
                        <span className="font-['Manrope:Bold',_sans-serif] font-bold">upload images</span>
                      </p>
                    </div>
                    <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-[#55778a]">
                      <p className="block whitespace-pre leading-[19.5px]">Supported formats: PNG, JPG, SVG</p>
                    </div>
                  </div>
                }
              />
              <div className="text-center">
                <FilePicker
                  as="button"
                  className="font-bold text-[#0d9da4] hover:underline"
                  onUploadDone={handleVideoUpload}
                  disabled={uploadFetching}
                  maxFiles={Math.max(1, MAX_GALLERY_IMAGES - (formData.settings.images?.length || 0))}
                  accept={["video/*"]}
                  fromSources={["local_file_system", "url", "googledrive", "dropbox"]}>
                  or upload a video
                </FilePicker>
              </div>
            </div>
          )}
        </div>

        {/* Section Background Color Section - Commented out - now using bgLight from theme
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Section background color</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Include a background color in this section.</p>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center">
              <Toggle
                checked={formData.settings.showBackgroundColor || false}
                onChange={(checked) => handleSettingsChange("showBackgroundColor", checked)}
              />
              <Typography variant="small" className="ml-3 font-medium text-gray-dark">
                Background color
              </Typography>
            </div>

            {formData.settings.showBackgroundColor && (
              <SmartColorInput
                label="Background Color"
                value={formData.settings.backgroundColor}
                onChange={(value) => handleSettingsChange("backgroundColor", value)}
                className="mt-4"
              />
            )}
          </div>
        </div>
        */}
      </div>

      {/* Footer Buttons */}
      <EditorFooter onDelete={onDelete} onCancel={handleCancelClick} onSave={handleSubmit} showDelete={isEditing} />

      {/* Confirm Save Modal */}
      <ConfirmSaveModal
        visible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSave}
      />
    </>
  )
}

export default GalleryEditor
